import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AudioLines, Disc3, Drama, Mic2, Music2, PartyPopper, Star, Theater } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CitySearchField from "../components/CitySearchField";
import api, { fmtINRFull, mediaUrl, pickArtistThumb } from "../lib/api";
import ArtistCardThumb from "../components/ArtistCardThumb";
import ArtistListingCard from "../components/ArtistListingCard";
import OccasionSection from "../components/OccasionSection";
import DatePlanningSection from "../components/DatePlanningSection";
import { BUDGET_RANGES } from "../lib/budgetRanges";
import { loadIndianCityCatalog } from "../lib/indianCities";
import { track } from "../analytics/tracker";

const CAT_TILES = [
  { slug: "singer",     icon: Mic2, name: "Singers & Vocalists" },
  { slug: "dj",         icon: Disc3, name: "DJs & Music Producers" },
  { slug: "comedian",   icon: Drama, name: "Stand-up Comedians" },
  { slug: "anchor",     icon: AudioLines, name: "Anchors & Emcees" },
  { slug: "dancer",     icon: Theater, name: "Dancers & Troupes" },
  { slug: "magician",   icon: PartyPopper, name: "Magicians & Illusionists" },
  { slug: "band",       icon: Music2, name: "Live Bands & Orchestras" },
  { slug: "celebrity",  icon: Star, name: "Celebrity Performers" },
];

const PINNED_CITY_TILES = [
  { slug: "mumbai", name: "Mumbai", cityKey: "mumbai", icon: "" },
  { slug: "delhi", name: "Delhi / NCR", cityKey: "delhi", icon: "" },
];
const HOME_CITY_TILE_LIMIT = 6;

function getHomeCityTiles(popular, remaining) {
  const allCities = [...popular, ...remaining];
  const citiesBySlug = new Map(allCities.map((city) => [city.slug, city]));
  const pinned = PINNED_CITY_TILES.map((tile) => ({
    ...citiesBySlug.get(tile.slug),
    ...tile,
  }));
  const pinnedSlugs = new Set(PINNED_CITY_TILES.map((tile) => tile.slug));
  const ranked = [...popular]
    .filter((city) => !pinnedSlugs.has(city.slug))
    .sort((a, b) => b.artist_count - a.artist_count || a.name.localeCompare(b.name));
  const fallback = remaining.filter((city) => !pinnedSlugs.has(city.slug));
  return [...pinned, ...ranked, ...fallback]
    .filter((city) => city.slug)
    .slice(0, HOME_CITY_TILE_LIMIT)
    .map((city) => ({
      ...city,
      cityKey: city.cityKey || city.name.trim().toLocaleLowerCase(),
      icon: city.icon || "",
    }));
}

function normalizeCityTileName(value) {
  return (value || "")
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase()
    .replace(/[\s-]+/g, "");
}

const BOOKING_CONFIDENCE = [
  { icon: "◈", title: "Payment protection", description: "Pay an advance to secure your date. Pay the balance as scheduled." },
  { icon: "↻", title: "Dedicated booking support", description: "Get help with booking questions and event coordination." },
  { icon: "✓", title: "Verified artists", description: "Every artist is reviewed before joining BookTalent." },
];

const BOOKING_STEPS = [
  { number: "01", title: "Pick or plan", description: "Browse artists or try the AI Event Planner." },
  { number: "02", title: "Pay the advance", description: "Secure your date with an advance." },
  { number: "03", title: "Artist confirms", description: "Coordinate details with your artist." },
  { number: "04", title: "Enjoy the show", description: "Enjoy your event; pay the balance as scheduled." },
];

const DEFAULT_CINEMATIC_CLIPS = [
  { name: "Sangeet", title: "Royal Wedding Sangeet · Udaipur", className: "cinematic-clip-1" },
  { name: "Concert", title: "Live Concert · Delhi", className: "cinematic-clip-2" },
  { name: "Corporate", title: "Corporate Gala · Gurugram", className: "cinematic-clip-3" },
  { name: "Comedy", title: "Stand-up Evening · Indore", className: "cinematic-clip-4" },
  { name: "Birthday", title: "Birthday Celebration · Mumbai", className: "cinematic-clip-5" },
  { name: "College Fest", title: "College Fest · Bengaluru", className: "cinematic-clip-6" },
  { name: "Reception", title: "Wedding Reception · Jaipur", className: "cinematic-clip-1" },
];

function requestDetectedCity() {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        api.get("/homepage/location-city", {
          params: { latitude: coords.latitude, longitude: coords.longitude },
        }).then(({ data }) => {
          resolve(typeof data?.city === "string" && data.city.trim() ? data.city.trim() : null);
        }).catch(reject);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          resolve(null);
          return;
        }
        reject(error);
      },
      { enableHighAccuracy: false, maximumAge: 600000, timeout: 10000 },
    );
  });
}

function getCategoryTiles(catStats, myCity) {
  const keywords = {
    singer: ["singer", "vocalist"],
    dj: ["dj"],
    comedian: ["comed"],
    anchor: ["anchor", "emcee"],
    dancer: ["danc"],
    magician: ["magic"],
    band: ["band", "orchestra"],
    celebrity: ["celebrity", "influencer"],
  };
  const tiles = CAT_TILES.map((category) => {
    const matches = keywords[category.slug] || [category.slug];
    const countMatches = (counts) => Object.entries(counts || {})
      .filter(([name]) => matches.some((keyword) => name.includes(keyword)))
      .reduce((total, [, count]) => total + count, 0);
    return {
      ...category,
      localCount: countMatches(catStats?.counts),
      nationwideCount: countMatches(catStats?.nationwide_counts),
    };
  });
  const populatedTiles = tiles.filter((tile) => tile.nationwideCount > 0);
  if (myCity && catStats) populatedTiles.sort((a, b) => b.localCount - a.localCount);
  return populatedTiles;
}

function CategoryTile({ category, myCity }) {
  const Icon = category.icon;

  return (
    <Link
      to={`/artists/${category.slug}`}
      className={`cat-card ${myCity && category.localCount > 0 ? "cat-card-local" : ""}`}
      data-testid={`cat-tile-${category.slug}`}
    >
      <span className="cat-icon" aria-hidden="true"><Icon size={21} strokeWidth={1.8} /></span>
      <span className="cat-name">{category.name}</span>
    </Link>
  );
}

function AccentedHeading({ title }) {
  const words = (title || "").trim().split(/\s+/);
  const accent = words.pop();

  return (
    <>
      {words.length > 0 && `${words.join(" ")} `}
      <span>{accent}</span>
    </>
  );
}

function CustomerStoriesSection({ stories }) {
  const [activeStory, setActiveStory] = useState(0);
  const selectStory = (index) => {
    setActiveStory((index + stories.length) % stories.length);
  };

  if (!stories.length) return null;

  return (
    <section className="customer-stories-section" data-testid="landing-testimonials">
      <div className="customer-stories-inner">
        <header className="customer-stories-header">
          <div>
            <span className="landing-section-eyebrow">Get inspiration for your event</span>
            <h2 className="landing-section-heading">
              Loved by <span>event planners</span> across India
            </h2>
          </div>
          <div className="customer-stories-controls" aria-label="Customer story controls">
            <button type="button" onClick={() => selectStory(activeStory - 1)} aria-label="Previous story">
              ‹
            </button>
            <button type="button" onClick={() => selectStory(activeStory + 1)} aria-label="Next story">
              ›
            </button>
          </div>
        </header>
        <div className="customer-stories-carousel" aria-label="Customer testimonials">
          {stories.map((story, index) => {
            const isActive = index === activeStory;
            const initials = story.name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
            // Full-size image: the 400 px thumbnail looked blurry stretched across the card.
            const imageSrc = /^https?:\/\//i.test(story.image || "")
              ? story.image
              : story.image_full === "file"
                ? `${mediaUrl(story.image)}/file`
                : mediaUrl(story.image);

            // The open story links to artists who perform at its event type
            // (set per story in admin). A custom link overrides it; stories
            // with neither open the full artist listing.
            const storyLink = story.artists_link
              || (story.event_type
                ? `/search?${new URLSearchParams({ event_type: story.event_type, fresh_search: "1" }).toString()}`
                : "/search?fresh_search=1");
            if (isActive) {
              return (
                <Link
                  className="customer-story-card is-active"
                  key={story.id}
                  to={storyLink}
                  onClick={() => track("customer_story_click", { story_id: story.id, event_type: story.event_type || undefined })}
                  aria-label={`${story.name}: explore artists for this event`}
                  data-testid={`testi-${story.id}`}
                >
                  <img className="customer-story-image" src={imageSrc} alt="" loading="lazy" />
                  <span className="customer-story-content">
                    <span className="customer-story-quote">“{story.quote}”</span>
                    <span className="customer-story-person">
                      <span className={`testi-avatar tav${(index % 3) + 1}`} aria-hidden="true">{initials}</span>
                      <span>
                        <span className="testi-name">{story.name}</span>
                        <span className="testi-role">{story.role}</span>
                      </span>
                    </span>
                    <span className="customer-story-cta" data-testid={`testi-cta-${story.id}`}>
                      {story.event_type ? `Explore ${story.event_type} artists →` : "Explore artists →"}
                    </span>
                  </span>
                </Link>
              );
            }

            return (
              <button
                type="button"
                className="customer-story-card"
                key={story.id}
                onClick={() => selectStory(index)}
                onMouseEnter={() => selectStory(index)}
                aria-label={`Show story from ${story.name}, ${story.role}`}
                aria-pressed={false}
                data-testid={`testi-${story.id}`}
              >
                <img className="customer-story-image" src={imageSrc} alt="" loading="lazy" />
                <span className="customer-story-preview" aria-hidden="true">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [rails, setRails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [popularCities, setPopularCities] = useState([]);
  const [cityAliases, setCityAliases] = useState({});
  const [homeCityTiles, setHomeCityTiles] = useState(PINNED_CITY_TILES);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [citiesError, setCitiesError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(false);
  const [myCity, setMyCity] = useState("");
  const [catStats, setCatStats] = useState(null);
  const [spotlight, setSpotlight] = useState({ cards: [], latest_booking: null });
  const [spotIdx, setSpotIdx] = useState(0);
  const [customerStories, setCustomerStories] = useState([]);
  const [artistsByCategory, setArtistsByCategory] = useState(null);
  // Iter 46 — homepage advanced search (date + city + category)
  const [advDate, setAdvDate] = useState("");
  const [advCategory, setAdvCategory] = useState("");
  const [advBudget, setAdvBudget] = useState("");

  useEffect(() => {
    api.get("/homepage/customer-stories")
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Homepage customer stories response is not a list");
        setCustomerStories(data);
      })
      .catch((error) => {
        console.warn("Unable to load homepage customer stories.", error);
      });
    api.get("/homepage/artists-by-category")
      .then(({ data }) => setArtistsByCategory(data || { total: 0, all: [], categories: [] }))
      .catch((error) => {
        console.warn("Unable to load homepage artists by category.", error);
        setArtistsByCategory({ total: 0, all: [], categories: [] });
      });
    api.get("/homepage/spotlight")
      .then(({ data }) => setSpotlight(data || { cards: [] }))
      .catch((error) => {
        console.warn("Unable to load homepage spotlight artists.", error);
      });
    // Sprint 5 — dynamic homepage rails
    api.get("/homepage/sections?limit=8").then((r) => {
      const baseRails = r.data || [];
      setRails((current) => [
        ...baseRails,
        ...current.filter((rail) => rail.code?.startsWith("city_")),
      ]);
    }).catch(() => setRails([])).finally(() => setLoading(false));
    loadIndianCityCatalog("event")
      .then(({ popular, cities: remaining, aliases }) => {
        setPopularCities(popular.map((city) => city.name));
        setCities([...popular, ...remaining].map((city) => city.name));
        setCityAliases(aliases);
        setHomeCityTiles(getHomeCityTiles(popular, remaining));
      })
      .catch((error) => {
        console.warn("Unable to load Indian city options for homepage search.", error);
        setCitiesError(true);
      })
      .finally(() => setCitiesLoading(false));
    api.get("/catalog/categories")
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Artist categories response is not a list");
        setCategories(data);
      })
      .catch((error) => {
        console.warn("Unable to load artist category options for homepage search.", error);
        setCategoriesError(true);
      })
      .finally(() => setCategoriesLoading(false));
    api.get("/homepage/category-stats")
      .then(r => setCatStats(r.data || null)).catch(() => setCatStats(null));
    requestDetectedCity().then((detectedCity) => {
      if (!detectedCity) return;
      setMyCity(detectedCity);
      api.get("/homepage/category-stats", { params: { city: detectedCity } })
        .then(({ data }) => setCatStats(data || null))
        .catch((error) => {
          console.warn("Unable to load local artist category counts.", error);
        });
      return api.get("/homepage/city-section", { params: { city: detectedCity } })
        .then(({ data }) => {
          if (data?.items?.length) {
            setRails((current) => [
              ...current.filter((rail) => !rail.code.startsWith("city_")),
              data,
            ]);
          }
        });
    }).catch((error) => {
      console.warn("Unable to show the location-based artist section.", error);
    });
  }, []);

  // Iter 45 — send an impression for each spotlight card shown (deduped by
  // per-session key + per-day on the backend, so rotation doesn't inflate).
  useEffect(() => {
    if (!spotlight.cards || spotlight.cards.length === 0) return;
    let sess = null;
    try {
      sess = localStorage.getItem("bt_session");
      if (!sess) {
        sess = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("bt_session", sess);
      }
    } catch {
      sess = Math.random().toString(36).slice(2);
    }
    spotlight.cards.forEach((c) => {
      if (c.user_id) {
        api.post("/homepage/spotlight/impression", { user_id: c.user_id, session: sess })
          .catch(() => {});
      }
    });
  }, [spotlight.cards]);

  // Rotate the hero spotlight cards every 5s when there are more than three.
  useEffect(() => {
    if (!spotlight.cards || spotlight.cards.length <= 3) return;
    const t = setInterval(() => {
      setSpotIdx((i) => (i + 1) % spotlight.cards.length);
    }, 5000);
    return () => clearInterval(t);
  }, [spotlight.cards]);

  // 3 rotating cards derived from the spotlight pool.
  const visibleSpotlight = (() => {
    const arr = spotlight.cards || [];
    if (arr.length <= 3) return arr;
    return [arr[spotIdx % arr.length], arr[(spotIdx + 1) % arr.length], arr[(spotIdx + 2) % arr.length]];
  })();
  const categoryTiles = getCategoryTiles(catStats, myCity);
  const remainingRails = rails.filter((rail) => rail.code !== "featured");

  const search = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) {
      p.set("city", city);
    }
    if (advDate) p.set("date", advDate);
    if (advCategory) p.set("category", advCategory);
    const budgetRange = BUDGET_RANGES.find((range) => range.value === advBudget);
    if (budgetRange) {
      p.set("min_price", budgetRange.min);
      if (budgetRange.max !== null) p.set("max_price", budgetRange.max);
    }
    p.set("fresh_search", "1");
    window.location.href = `/search?${p.toString()}`;
  };

  return (
    <div className="landing-page" data-testid="landing-page">
      <SEO
        title="Book India's Finest Talent, On Demand"
        description="Book verified singers, DJs, comedians, dancers and anchors for weddings, corporate events, birthdays and private parties. Transparent 5% platform fee, direct settlement with your artist."
        keywords="book artists india, book singer, book DJ, book comedian, wedding entertainment, corporate event artists, book anchor, book dancer"
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "BookTalent",
          url: "https://booktalent.in",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://booktalent.in/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="orb orb-3" />
      <Nav />

      <section className="hero hero-split" data-testid="hero-split">
        <div className="landing-hero-content">
        <div className="hero-left">
          <h1>
            India's<br/>
            <span className="gold-grad">Finest Talent,</span><br/>
            <span className="gold-grad italic">On Demand</span>
          </h1>
          <div className="hero-cta-row" data-testid="hero-cta-row">
            <Link to="/planner" className="btn btn-gold btn-lg" data-testid="hero-cta-planner">
              AI Event Planner
            </Link>
            <Link to="/signup?role=artist" className="btn btn-purple btn-lg" data-testid="hero-cta-list">
              Join as Artist →
            </Link>
          </div>
          <LandingSearch
            idPrefix="hero-adv"
            date={advDate}
            city={city}
            category={advCategory}
            budget={advBudget}
            cities={cities}
            popularCities={popularCities}
            cityAliases={cityAliases}
            citiesLoading={citiesLoading}
            citiesError={citiesError}
            categories={categories}
            categoriesLoading={categoriesLoading}
            categoriesError={categoriesError}
            onDateChange={setAdvDate}
            onCityChange={setCity}
            onCategoryChange={setAdvCategory}
            onBudgetChange={setAdvBudget}
            onSearch={search}
          />
        </div>

        <div className="hero-right" data-testid="hero-spotlight">
          {(spotlight.cards || []).length === 0 && (
            <>
              <div className="sk sk-spotlight-card sk-spot-1" data-testid="sk-spot-1" />
              <div className="sk sk-spotlight-card sk-spot-2" data-testid="sk-spot-2" />
              <div className="sk sk-spotlight-card sk-spot-3" data-testid="sk-spot-3" />
            </>
          )}
          {visibleSpotlight.slice(0, 3).map((c, i) => {
            // Featured artists pay for hero placement — show their actual photo.
            // Priority: profile_image → cover_image → first gallery thumb → emoji fallback.
            const thumbs = c.gallery_thumbs || [];
            const featuredThumb = thumbs.find((t) => t.is_featured) || thumbs[0];
            const heroImg = c.profile_image
              ? mediaUrl(c.profile_image)
              : c.cover_image
                ? mediaUrl(c.cover_image)
                : (featuredThumb ? mediaUrl(featuredThumb.id) : null);
            return (
            <Link
              key={`${c.user_id}-${spotIdx}`}
              to={`/artist/${c.slug || c.user_id}`}
              className={`spotlight-card spotlight-card-${i + 1}`}
              data-testid={`spotlight-card-${i}`}
              target="_blank"
              rel="noopener noreferrer"
              {...(heroImg ? {
                style: {
                  background: `linear-gradient(180deg, rgba(36,22,58,0) 45%, rgba(36,22,58,0.35) 100%), url(${heroImg}) center/cover no-repeat`,
                },
              } : {})}
            >
              {!heroImg && (
                <div className="spotlight-emoji" aria-hidden>
                  {c.emoji || ""}
                </div>
              )}
              <div className="spotlight-body">
                {c.kyc_status === "approved" && <span className="spotlight-badge">✓ Verified Artist</span>}
                <div className="spotlight-name">{c.stage_name}</div>
                <div className="spotlight-cat">{c.category}</div>
                <div className="spotlight-foot">
                  {c.starting_price && <span className="spotlight-price">{fmtINRFull(c.starting_price)}</span>}
                </div>
              </div>
            </Link>
          );})}
          {spotlight.latest_booking && (
            <div className="spotlight-toast" data-testid="spotlight-toast">
              <div>
                <div className="spotlight-toast-label">BOOKING CONFIRMED</div>
                <div className="spotlight-toast-title" data-testid="spotlight-booking-artist">
                  {spotlight.latest_booking.artist_name}
                  {spotlight.latest_booking.artist_category && (
                    <span className="spotlight-toast-category"> · {spotlight.latest_booking.artist_category}</span>
                  )}
                </div>
              </div>
              <div className="spotlight-toast-sub" data-testid="spotlight-booking-location">
                {[spotlight.latest_booking.city, spotlight.latest_booking.event_date && new Date(`${spotlight.latest_booking.event_date}T00:00:00Z`).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" })]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          )}
        </div>
        </div>
      </section>

      <CustomerStoriesSection stories={customerStories} />

      {/* ── Categories grid ───────────────────────────────────────────── */}
      <section className="lp-section" data-testid="landing-categories">
        <div className="category-discovery-panel">
          <div className="section-header category-section-header">
            <div>
              <span className="landing-section-eyebrow">Explore the possibilities</span>
              <h2 className="section-title landing-section-heading">
                Every kind of <em className="category-heading-accent">talent</em> you need
              </h2>
            </div>
            <Link to="/search" className="btn btn-ghost category-view-all" data-testid="categories-view-all">View all →</Link>
          </div>
          <div className="cat-grid category-card-row">
            {categoryTiles
              .filter((category) => !["singer", "dj"].includes(category.slug))
              .map((category) => (
                <CategoryTile key={category.slug} category={category} myCity={myCity} />
              ))}
          </div>
        </div>
      </section>

      <CategoryArtistsSection data={artistsByCategory} />

      <OccasionSection />
      <DatePlanningSection />

      {!loading && remainingRails.some((rail) => rail?.items?.length > 0) && (
        <section className="section landing-home-rails">
          <div className="container">
            {remainingRails.map((rail) => (
              rail.code.startsWith("cat_")
                ? <PopularCategoryRail key={rail.code} rail={rail} />
                : <HomeRail key={rail.code} rail={rail} />
            ))}
          </div>
        </section>
      )}

      {/* ── Cities grid ──────────────────────────────────────────────── */}
      <section className="lp-section" style={{ textAlign: "center" }} data-testid="landing-cities">
        <span className="landing-section-eyebrow">Explore by location</span>
        <h2 className="section-title landing-section-heading" style={{ margin: "0 auto var(--space-6)", maxWidth: 620 }}>
          Artists in your <span>city</span>
        </h2>
        <div className="cities-grid">
          {(() => {
            const my = normalizeCityTileName(myCity);
            const ordered = [...homeCityTiles].sort((a, b) => {
              const am = my && (normalizeCityTileName(a.name) === my || normalizeCityTileName(a.slug) === my) ? -1 : 0;
              const bm = my && (normalizeCityTileName(b.name) === my || normalizeCityTileName(b.slug) === my) ? -1 : 0;
              return am - bm;
            });
            return ordered.map((c) => {
              const isMine = my && (normalizeCityTileName(c.name) === my || normalizeCityTileName(c.slug) === my);
              return (
                <Link
                  key={c.slug}
                  to={`/artists/city/${c.slug}`}
                  className={`city-card ${isMine ? "city-card-mine" : ""}`}
                  data-testid={`city-tile-${c.slug}`}
                >
                  {isMine && <span className="city-mine-badge"> Your city</span>}
                  <div className="city-icon">{c.icon}</div>
                  <div className="city-name">{c.name}</div>
                  <div className="city-count">Explore artists</div>
                </Link>
              );
            });
          })()}
        </div>
      </section>

      {/* ── Booking confidence ───────────────────────────────────────── */}
      <section className="lp-section booking-confidence-section" data-testid="landing-how-it-works">
        <div className="booking-confidence-panel">
          <header className="booking-confidence-head">
            <span className="booking-confidence-eyebrow landing-section-eyebrow">Book with confidence</span>
            <h2 className="landing-section-heading">Your event is <span>protected</span></h2>
          </header>
          <div className="booking-trust-grid">
            {BOOKING_CONFIDENCE.map((item, index) => (
              <article className="booking-trust-card" key={item.title} data-testid={`booking-trust-${index + 1}`}>
                <span className="booking-trust-icon" aria-hidden="true">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
          <ol className="booking-steps">
            {BOOKING_STEPS.map((step) => (
              <li className="booking-step" key={step.number} data-testid={`booking-step-${step.number}`}>
                <span className="booking-step-label">Step {step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Artist Join CTA ─────────────────────────────────────────── */}
      <section className="lp-section" data-testid="landing-dual-cta">
        <div className="dual-cta" style={{ gridTemplateColumns: "1fr" }}>
          <div className="cta-box" data-testid="cta-artist">
            <div className="cta-box-glow cta-glow-purple" />
            <span className="cta-box-icon"></span>
            <div className="cta-box-title">Are you an Artist?</div>
            <div className="cta-box-desc">
              Join verified performers on India's premium marketplace. Manage your profile, calendar, packages and bookings — all from one clean dashboard.
            </div>
            <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
              <Link to="/signup?role=artist" className="btn btn-purple" data-testid="cta-artist-join">Join as Artist →</Link>
              <Link to="/page/artist-guidelines" className="btn btn-ghost" data-testid="cta-artist-learn">Read the Artist Guidelines</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function LandingSearch({
  idPrefix,
  date,
  city,
  category,
  budget,
  cities,
  popularCities = [],
  cityAliases = {},
  citiesLoading = false,
  citiesError = false,
  categories = [],
  categoriesLoading = false,
  categoriesError = false,
  onDateChange,
  onCityChange,
  onCategoryChange,
  onBudgetChange,
  onSearch,
  className = "",
  showPlanner = false,
}) {
  const dateId = `${idPrefix}-date`;
  const cityId = `${idPrefix}-city`;
  const categoryId = `${idPrefix}-category`;
  const budgetId = `${idPrefix}-budget`;

  return (
    <div
      className={`hero-adv-search ${className}`.trim()}
      data-testid={`${idPrefix}-search`}
    >
      <div className="hero-adv-field">
        <label htmlFor={dateId}>Event Date</label>
        <input
          id={dateId}
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          min={new Date().toISOString().split("T")[0]}
          data-testid={`${idPrefix}-date`}
        />
      </div>
      <div className="hero-adv-field">
        <label htmlFor={cityId}>City</label>
        <CitySearchField
          id={cityId}
          city={city}
          cities={cities}
          popularCities={popularCities}
          cityAliases={cityAliases}
          onCityChange={onCityChange}
          listId={`${cityId}-options`}
          allowCustom
          disabled={citiesLoading || citiesError}
          placeholder={citiesLoading ? "Loading cities…" : citiesError ? "Cities unavailable" : "Search city"}
          data-testid={`${idPrefix}-city`}
        />
        {citiesError && <span className="hero-adv-error" role="status">City list is unavailable. Please refresh to try again.</span>}
      </div>
      <div className="hero-adv-field">
        <label htmlFor={categoryId}>Artist Type</label>
        <select
          id={categoryId}
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          disabled={categoriesLoading || categoriesError}
          data-testid={`${idPrefix}-category`}
        >
          <option value="">{categoriesLoading ? "Loading artist types…" : categoriesError ? "Artist types unavailable" : "Select category"}</option>
          {categories.map((item) => (
            <option key={item.slug || item.id || item.name} value={item.name}>{item.name}</option>
          ))}
        </select>
        {categoriesError && <span className="hero-adv-error" role="status">Artist types are unavailable. Please refresh to try again.</span>}
      </div>
      <div className="hero-adv-field">
        <label htmlFor={budgetId}>Budget</label>
        <select
          id={budgetId}
          value={budget}
          onChange={(e) => onBudgetChange(e.target.value)}
          data-testid={`${idPrefix}-budget`}
        >
          <option value="">Your budget</option>
          {BUDGET_RANGES.map((range) => (
            <option key={range.value} value={range.value}>{range.label}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className="btn btn-gold hero-adv-btn"
        onClick={onSearch}
        data-testid={`${idPrefix}-search-btn`}
      >
        Find Artists →
      </button>
      {showPlanner && (
        <Link to="/planner" className="btn btn-ghost cinematic-planner">
          ✨ AI Event Planner
        </Link>
      )}
    </div>
  );
}

function CinematicHero({
  clips,
  activeClip,
  onSelectClip,
  date,
  city,
  category,
  cities,
  citiesLoading,
  citiesError,
  categories,
  categoriesLoading,
  categoriesError,
  onDateChange,
  onCityChange,
  onCategoryChange,
  onSearch,
}) {
  const selectedClip = Math.min(activeClip, Math.max(0, clips.length - 1));
  const active = clips[selectedClip] || DEFAULT_CINEMATIC_CLIPS[0];

  return (
    <section className="cinematic-section" data-testid="cinematic-hero">
      <div className="cinematic-stage">
        {active.id && (
          <video
            key={active.id}
            className="cinematic-active-video"
            src={`${mediaUrl(active.id)}/file`}
            poster={`${mediaUrl(active.id)}/thumb`}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={active.title || "Featured event video"}
            data-testid="cinematic-active-video"
          />
        )}
        <div className={`cinematic-backdrop ${active.className} ${active.id ? "with-video" : ""}`} aria-hidden="true" />
        <div className="cinematic-caption">
          <div className="cinematic-eyebrow">Now playing</div>
          <h2>{active.title}</h2>
        </div>
        <span className="cinematic-play" aria-hidden="true" />
        <div className="cinematic-dock">
          <div className="cinematic-thumbs" aria-label="Featured event highlights">
            {clips.map((clip, index) => (
              <button
                type="button"
                key={`${clip.name}-${index}`}
                className={`cinematic-thumb ${clip.className} ${index === selectedClip ? "active" : ""}`}
                onClick={() => onSelectClip(index)}
                aria-label={`Show ${clip.name} highlight`}
                aria-pressed={index === selectedClip}
              >
                {clip.id && (
                  <img
                    src={`${mediaUrl(clip.id)}/thumb`}
                    alt=""
                    loading="lazy"
                    className="cinematic-thumb-poster"
                  />
                )}
                <span className="cinematic-thumb-label">{clip.name}</span>
              </button>
            ))}
          </div>
          <LandingSearch
            idPrefix="cinematic-search"
            date={date}
            city={city}
            category={category}
            cities={cities}
            citiesLoading={citiesLoading}
            citiesError={citiesError}
            categories={categories}
            categoriesLoading={categoriesLoading}
            categoriesError={categoriesError}
            onDateChange={onDateChange}
            onCityChange={onCityChange}
            onCategoryChange={onCategoryChange}
            onSearch={onSearch}
            className="cinematic-search"
            showPlanner
          />
        </div>
      </div>
     
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Sprint 5 — Dynamic homepage rail. Horizontal-scrolling artist row.
// ────────────────────────────────────────────────────────────────────────
function HomeRail({ rail }) {
  // Iter 45 — inline filter chips (category / city / price band)
  const [fCat, setFCat] = React.useState("all");
  const [fCity, setFCity] = React.useState("all");
  const [fPrice, setFPrice] = React.useState("all");
  const [showFilters, setShowFilters] = React.useState(false);

  if (!rail?.items?.length) return null;
  // Dedupe by user_id to guard against duplicate-key warnings
  const seen = new Set();
  const items = rail.items.filter((a) => {
    if (!a?.user_id || seen.has(a.user_id)) return false;
    seen.add(a.user_id);
    return true;
  });

  const cats = ["all", ...Array.from(new Set(items.map((a) => a.category).filter(Boolean))).slice(0, 5)];
  const cities = ["all", ...Array.from(new Set(items.map((a) => a.city).filter(Boolean))).slice(0, 5)];
  const PRICE_BANDS = [
    { id: "all",   label: "Any price", test: () => true },
    { id: "low",   label: "Under ₹25k",  test: (p) => p && p < 25000 },
    { id: "mid",   label: "₹25k – ₹75k", test: (p) => p && p >= 25000 && p < 75000 },
    { id: "high",  label: "₹75k – ₹2L",  test: (p) => p && p >= 75000 && p < 200000 },
    { id: "elite", label: "₹2L+",         test: (p) => p && p >= 200000 },
  ];

  const filtered = items.filter((a) => {
    if (fCat !== "all" && a.category !== fCat) return false;
    if (fCity !== "all" && a.city !== fCity) return false;
    const band = PRICE_BANDS.find((b) => b.id === fPrice);
    if (band && !band.test(a.starting_price)) return false;
    return true;
  });
  const anyActive = fCat !== "all" || fCity !== "all" || fPrice !== "all";
  const activeCount = [fCat, fCity, fPrice].filter((v) => v !== "all").length;
  const hasFilterableFacets = cats.length > 2 || cities.length > 2;
  return (
    <div className="mb-32" data-testid={`rail-${rail.code}`}>
      <div className="section-head">
        <div>
          <span className="landing-section-eyebrow">Discover performers</span>
          <h2 className="section-title landing-section-heading">
            <AccentedHeading title={rail.title} />
          </h2>
          {rail.subtitle && rail.code !== "featured" && !rail.code.startsWith("city_") && (
            <p className="section-sub">{rail.subtitle}</p>
          )}
        </div>
        <div className="section-head-actions">
          {hasFilterableFacets && (
            <button
              type="button"
              className={`btn btn-ghost btn-sm rail-filter-toggle ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters((s) => !s)}
              data-testid={`rail-filter-toggle-${rail.code}`}
              aria-expanded={showFilters}
            >
              {showFilters ? "▲" : "▼"} Filter{activeCount > 0 && <span className="rail-filter-count">{activeCount}</span>}
            </button>
          )}
          <Link to={`/search?section=${encodeURIComponent(rail.code)}`} className="btn btn-ghost btn-sm" data-testid={`rail-more-${rail.code}`}>View All →</Link>
        </div>
      </div>

      {hasFilterableFacets && showFilters && (
        <div className="rail-filters" data-testid={`rail-filters-${rail.code}`}>
          {cats.length > 2 && (<>
            <span className="rail-filter-label">Type</span>
            {cats.map((c) => (
              <button key={c} className={`rail-chip ${fCat === c ? "active" : ""}`} onClick={() => setFCat(c)} data-testid={`rail-cat-${rail.code}-${c}`}>
                {c === "all" ? "All" : c}
              </button>
            ))}
          </>)}
          {cities.length > 2 && (<>
            <span className="rail-filter-label" style={{ marginLeft: 8 }}>City</span>
            {cities.map((c) => (
              <button key={c} className={`rail-chip ${fCity === c ? "active" : ""}`} onClick={() => setFCity(c)} data-testid={`rail-city-${rail.code}-${c}`}>
                {c === "all" ? "All" : c}
              </button>
            ))}
          </>)}
          <span className="rail-filter-label" style={{ marginLeft: 8 }}>Price</span>
          {PRICE_BANDS.map((b) => (
            <button key={b.id} className={`rail-chip ${fPrice === b.id ? "active" : ""}`} onClick={() => setFPrice(b.id)} data-testid={`rail-price-${rail.code}-${b.id}`}>
              {b.label}
            </button>
          ))}
          {anyActive && (
            <button className="rail-chip" onClick={() => { setFCat("all"); setFCity("all"); setFPrice("all"); }} data-testid={`rail-clear-${rail.code}`} style={{ color: "var(--gold-light)" }}>
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rail-empty" data-testid={`rail-empty-${rail.code}`}>
          No matches — try relaxing the filters.
        </div>
      ) : (
      <div className="artist-grid-v2">
        {filtered.map((artist) => (
          <ArtistListingCard
            key={artist.user_id}
            artist={artist}
            testId={`rail-card-${rail.code}-${artist.user_id}`}
          />
        ))}
      </div>
      )}
    </div>
  );
}

// Every live artist, one tab per category (featured artists lead each tab).
function CategoryArtistsSection({ data }) {
  const [active, setActive] = React.useState("all");

  if (!data) {
    return (
      <section className="section landing-home-rails" data-testid="category-artists-loading">
        <div className="container">
          <div className="sk sk-line" style={{ width: 220, height: 28, marginBottom: "var(--space-4)" }} />
          <div className="artist-grid-v2">
            {[...Array(4)].map((_, i) => (
              <div key={`sk-cat-${i}`} className="sk-artist-card">
                <div className="sk sk-cover" />
                <div className="sk-body">
                  <div className="sk sk-line mid" />
                  <div className="sk sk-line short" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  if (!data.total) return null;

  const category = data.categories.find((item) => item.name === active);
  const items = category ? category.items : data.all;
  const viewAll = category
    ? `/search?${new URLSearchParams({ category: category.name, fresh_search: "1" }).toString()}`
    : "/search?fresh_search=1";

  return (
    <section className="section landing-home-rails" data-testid="category-artists">
      <div className="container">
        <div className="section-head">
          <div>
            <span className="landing-section-eyebrow">Discover performers</span>
            <h2 className="section-title landing-section-heading">
              Our <span>Artists</span>
            </h2>
          </div>
          <div className="section-head-actions">
            <Link to={viewAll} className="btn btn-ghost btn-sm" data-testid="category-artists-view-all">
              View all →
            </Link>
          </div>
        </div>
        <div className="category-artist-tabs" role="tablist" aria-label="Artist categories">
          {[{ name: "all", label: "All", count: data.total }, ...data.categories.map((item) => ({ ...item, label: item.name }))].map((tab) => (
            <button
              key={tab.name}
              type="button"
              role="tab"
              aria-selected={active === tab.name}
              className={`rail-chip ${active === tab.name ? "active" : ""}`}
              onClick={() => {
                setActive(tab.name);
                track("home_category_tab", { category: tab.name });
              }}
              data-testid={`category-tab-${tab.name}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Every artist in the tab, wrapping onto as many rows as needed. */}
        <div className="artist-grid-v2" data-testid="category-artists-grid">
          {items.map((artist) => (
            <ArtistListingCard key={artist.user_id} artist={artist} testId={`category-card-${artist.user_id}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PopularCategoryRail({ rail }) {
  const artists = (rail.items || []).filter((artist) => artist?.user_id);
  if (!artists.length) return null;

  return (
    <section className="popular-category-rail" data-testid={`popular-category-${rail.code}`}>
      <header className="popular-category-head">
        <div>
          <span className="landing-section-eyebrow">Popular categories</span>
          <h2 className="landing-section-heading">
            <AccentedHeading title={rail.title} />
          </h2>
        </div>
        <Link to={`/search?category=${encodeURIComponent(rail.title)}`}>
          See all {Number(rail.category_count || 0).toLocaleString("en-IN")} →
        </Link>
      </header>
      <div className="popular-category-cards">
        {artists.map((artist) => {
          const name = artist.stage_name || "Artist";
          const initials = name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
          return (
            <Link
              to={`/artist/${artist.slug || artist.user_id}`}
              key={artist.user_id}
              onClick={() => track("artist_card_click", { artist_id: artist.user_id, source: "home_section" })}
              className="popular-category-card"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArtistCardThumb
                artist={artist}
                className="popular-category-photo"
                placeholder={<span>{initials}</span>}
              />
              <span className="popular-category-card-body">
                <strong>{name}</strong>
                {artist.city && <small>{artist.city}</small>}
                <em>{artist.starting_price ? fmtINRFull(artist.starting_price) : "Contact for price"}</em>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
